/**
 * PDF Studio session store — document, tools, per-page annotation JSON, history.
 */

import { create } from 'zustand';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { PdfDocumentMeta, PdfSignature } from '@canvas/shared-types';
import type { PdfShapeKind, PdfToolId } from '@/data/pdfTools';
import type { FabricJson } from '@/lib/pdf/pdfAnnotations';
import { emptyFabricJson } from '@/lib/pdf/pdfAnnotations';
import {
  clearProtectionPassword,
  type PdfEncryptionAlgorithm,
  type PdfProtectionSettings,
} from '@/lib/pdf/pdfProtection';
import {
  clearAnnotationDrafts,
  getAnnotationDraft,
  mergeAnnotationDrafts,
  setAnnotationDraft,
} from '@/lib/pdf/pdfAnnotationDrafts';

const MAX_HISTORY = 40;

export type PdfZoomMode = 'auto' | number;

interface PageHistory {
  stack: FabricJson[];
  index: number;
}

export interface PdfSessionState {
  docId: string | null;
  meta: PdfDocumentMeta | null;
  name: string;
  sourceBytes: ArrayBuffer | null;
  pdfProxy: PDFDocumentProxy | null;
  pageCount: number;
  activePageIndex: number;
  thumbUrls: Record<number, string>;
  pageAnnotations: Record<number, FabricJson>;
  pageHistory: Record<number, PageHistory>;
  tool: PdfToolId;
  shapeKind: PdfShapeKind;
  color: string;
  highlightColor: string;
  brushSize: number;
  fontSize: number;
  zoomMode: PdfZoomMode;
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;
  busyAction: string | null;
  /** Bumps when undo/redo should force Fabric overlay reload */
  overlayEpoch: number;
  /** Signature library (session cache) */
  signatures: PdfSignature[];
  selectedSignatureId: string | null;
  signatureModalOpen: boolean;
  signaturesLoaded: boolean;
  /** Session-only export protection (password lives in pdfProtection module) */
  protection: PdfProtectionSettings | null;
  passwordProtectModalOpen: boolean;
  /**
   * Viewport registers a flush that commits the live Fabric overlay into
   * pageAnnotations before download / send-to-canvas.
   */
  flushActiveOverlay: (() => void) | null;
  /**
   * Viewport clears the live Fabric canvas without writing back to the store
   * (used after Finalize so baked ink is not re-layered as editable overlays).
   */
  clearActiveOverlay: (() => void) | null;
  /** When true, commitPageAnnotation is a no-op (Finalize / PDF swap). */
  suspendOverlayCommits: boolean;
}

export interface PdfStore extends PdfSessionState {
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setBusyAction: (action: string | null) => void;
  openSession: (payload: {
    name: string;
    sourceBytes: ArrayBuffer;
    pdfProxy: PDFDocumentProxy;
    docId?: string | null;
    meta?: PdfDocumentMeta | null;
    pageAnnotations?: Record<number, FabricJson>;
  }) => void;
  setMeta: (meta: PdfDocumentMeta) => void;
  setActivePage: (index: number) => void;
  setThumbUrl: (pageIndex: number, url: string) => void;
  setTool: (tool: PdfToolId) => void;
  setShapeKind: (kind: PdfShapeKind) => void;
  setColor: (color: string) => void;
  setHighlightColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setFontSize: (size: number) => void;
  setZoomMode: (mode: PdfZoomMode) => void;
  getPageAnnotation: (pageIndex: number) => FabricJson;
  commitPageAnnotation: (pageIndex: number, json: FabricJson, pushHistory?: boolean) => void;
  undoPage: (pageIndex: number) => FabricJson | null;
  redoPage: (pageIndex: number) => FabricJson | null;
  canUndo: (pageIndex: number) => boolean;
  canRedo: (pageIndex: number) => boolean;
  markSaved: () => void;
  setSignatures: (list: PdfSignature[]) => void;
  addSignature: (sig: PdfSignature) => void;
  removeSignature: (signatureId: string) => void;
  setSelectedSignatureId: (id: string | null) => void;
  setSignatureModalOpen: (open: boolean) => void;
  setSignaturesLoaded: (loaded: boolean) => void;
  getSelectedSignature: () => PdfSignature | null;
  setProtection: (protection: PdfProtectionSettings | null) => void;
  setPasswordProtectModalOpen: (open: boolean) => void;
  enableProtection: (algorithm: PdfEncryptionAlgorithm) => void;
  clearProtection: () => void;
  setFlushActiveOverlay: (fn: (() => void) | null) => void;
  setClearActiveOverlay: (fn: (() => void) | null) => void;
  resumeOverlayCommits: () => void;
  /** Commit live canvas, then return latest pageAnnotations snapshot */
  captureAnnotationsForExport: () => Record<number, FabricJson>;
  /** Replace source PDF after Finalize (baked annotations) and clear overlays */
  applyFinalizedPdf: (payload: {
    sourceBytes: ArrayBuffer;
    pdfProxy: PDFDocumentProxy;
  }) => void;
}

const initialState: PdfSessionState = {
  docId: null,
  meta: null,
  name: '',
  sourceBytes: null,
  pdfProxy: null,
  pageCount: 0,
  activePageIndex: 0,
  thumbUrls: {},
  pageAnnotations: {},
  pageHistory: {},
  tool: 'pointer',
  shapeKind: 'rect',
  color: '#c41e3a',
  highlightColor: '#f7e017',
  brushSize: 4,
  fontSize: 18,
  zoomMode: 'auto',
  isDirty: false,
  isLoading: false,
  error: null,
  busyAction: null,
  overlayEpoch: 0,
  signatures: [],
  selectedSignatureId: null,
  signatureModalOpen: false,
  signaturesLoaded: false,
  protection: null,
  passwordProtectModalOpen: false,
  flushActiveOverlay: null,
  clearActiveOverlay: null,
  suspendOverlayCommits: false,
};

function ensureHistory(
  map: Record<number, PageHistory>,
  pageIndex: number,
  seed: FabricJson
): PageHistory {
  if (map[pageIndex]) return map[pageIndex];
  return { stack: [seed], index: 0 };
}

export const usePdfStore = create<PdfStore>((set, get) => ({
  ...initialState,

  reset: () => {
    const prev = get().pdfProxy;
    try {
      void (prev as { destroy?: () => void | Promise<void> } | null)?.destroy?.();
    } catch {
      /* ignore */
    }
    clearProtectionPassword();
    clearAnnotationDrafts();
    // Keep signature library across editor remounts (React Strict Mode / reopen)
    const {
      signatures,
      selectedSignatureId,
      signaturesLoaded,
    } = get();
    set({
      ...initialState,
      signatures,
      selectedSignatureId,
      signaturesLoaded,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setBusyAction: (busyAction) => set({ busyAction }),

  openSession: ({ name, sourceBytes, pdfProxy, docId, meta, pageAnnotations }) => {
    const prev = get().pdfProxy;
    if (prev && prev !== pdfProxy) {
      try {
        void (prev as { destroy?: () => void | Promise<void> }).destroy?.();
      } catch {
        /* ignore */
      }
    }

    const annotations = pageAnnotations || {};
    const pageHistory: Record<number, PageHistory> = {};
    for (const key of Object.keys(annotations)) {
      const idx = Number(key);
      pageHistory[idx] = { stack: [annotations[idx]], index: 0 };
    }

    // Keep signature library + viewport flush hook across doc opens;
    // always clear protection when opening a different document.
    const {
      signatures,
      selectedSignatureId,
      signaturesLoaded,
      flushActiveOverlay,
      clearActiveOverlay,
    } = get();
    clearProtectionPassword();
    clearAnnotationDrafts();
    for (const [key, json] of Object.entries(annotations)) {
      const idx = Number(key);
      if (Number.isFinite(idx) && json) setAnnotationDraft(idx, json);
    }

    set({
      ...initialState,
      signatures,
      selectedSignatureId,
      signaturesLoaded,
      flushActiveOverlay,
      clearActiveOverlay,
      docId: docId ?? meta?.docId ?? null,
      meta: meta ?? null,
      name,
      sourceBytes,
      pdfProxy,
      pageCount: pdfProxy.numPages,
      pageAnnotations: annotations,
      pageHistory,
      isDirty: false,
      isLoading: false,
      error: null,
      protection: null,
      passwordProtectModalOpen: false,
    });
  },

  setMeta: (meta) => set({ meta, docId: meta.docId, name: meta.name }),

  setActivePage: (index) => {
    const { pageCount } = get();
    if (index < 0 || index >= pageCount) return;
    set({ activePageIndex: index });
  },

  setThumbUrl: (pageIndex, url) =>
    set((s) => ({ thumbUrls: { ...s.thumbUrls, [pageIndex]: url } })),

  setTool: (tool) => set({ tool }),
  setShapeKind: (shapeKind) => set({ shapeKind }),
  setColor: (color) => set({ color }),
  setHighlightColor: (highlightColor) => set({ highlightColor }),
  setBrushSize: (brushSize) => set({ brushSize: Math.max(1, Math.min(48, brushSize)) }),
  setFontSize: (fontSize) => set({ fontSize: Math.max(8, Math.min(96, fontSize)) }),
  setZoomMode: (zoomMode) => set({ zoomMode }),

  getPageAnnotation: (pageIndex) => {
    const draft = getAnnotationDraft(pageIndex);
    const existing = get().pageAnnotations[pageIndex];
    const draftCount = (draft?.objects as unknown[] | undefined)?.length ?? 0;
    const existingCount = (existing?.objects as unknown[] | undefined)?.length ?? 0;
    if (draft && draftCount > existingCount) {
      return draft;
    }
    if (existing) return existing;
    if (draft) return draft;
    return emptyFabricJson(1, 1);
  },

  commitPageAnnotation: (pageIndex, json, pushHistory = true) => {
    if (get().suspendOverlayCommits) return;
    // Deep-clone so later Fabric mutations / shared refs cannot thin stored overlays
    const snapshot = JSON.parse(JSON.stringify(json)) as typeof json;
    const nextCount = Array.isArray(snapshot.objects)
      ? (snapshot.objects as unknown[]).length
      : 0;
    const prev = get().pageAnnotations[pageIndex];
    const prevDraft = getAnnotationDraft(pageIndex);
    const prevCount = Math.max(
      Array.isArray(prev?.objects) ? (prev!.objects as unknown[]).length : 0,
      Array.isArray(prevDraft?.objects)
        ? (prevDraft!.objects as unknown[]).length
        : 0
    );
    // Silent flushes (page switch / export) must never replace a richer overlay
    // with a thinner/empty one. Explicit edits pushHistory=true and may delete.
    if (!pushHistory && nextCount < prevCount) {
      return;
    }
    setAnnotationDraft(pageIndex, snapshot);
    set((s) => {
      const pageAnnotations = { ...s.pageAnnotations, [pageIndex]: snapshot };
      if (!pushHistory) {
        return { pageAnnotations, isDirty: true };
      }
      const hist = ensureHistory(s.pageHistory, pageIndex, snapshot);
      const trimmed = hist.stack.slice(0, hist.index + 1);
      trimmed.push(snapshot);
      while (trimmed.length > MAX_HISTORY) trimmed.shift();
      const pageHistory = {
        ...s.pageHistory,
        [pageIndex]: { stack: trimmed, index: trimmed.length - 1 },
      };
      return { pageAnnotations, pageHistory, isDirty: true };
    });
  },

  undoPage: (pageIndex) => {
    const hist = get().pageHistory[pageIndex];
    if (!hist || hist.index <= 0) return null;
    const index = hist.index - 1;
    const json = hist.stack[index];
    setAnnotationDraft(pageIndex, json);
    set((s) => ({
      pageHistory: { ...s.pageHistory, [pageIndex]: { ...hist, index } },
      pageAnnotations: { ...s.pageAnnotations, [pageIndex]: json },
      isDirty: true,
      overlayEpoch: s.overlayEpoch + 1,
    }));
    return json;
  },

  redoPage: (pageIndex) => {
    const hist = get().pageHistory[pageIndex];
    if (!hist || hist.index >= hist.stack.length - 1) return null;
    const index = hist.index + 1;
    const json = hist.stack[index];
    setAnnotationDraft(pageIndex, json);
    set((s) => ({
      pageHistory: { ...s.pageHistory, [pageIndex]: { ...hist, index } },
      pageAnnotations: { ...s.pageAnnotations, [pageIndex]: json },
      isDirty: true,
      overlayEpoch: s.overlayEpoch + 1,
    }));
    return json;
  },

  canUndo: (pageIndex) => {
    const hist = get().pageHistory[pageIndex];
    return !!hist && hist.index > 0;
  },

  canRedo: (pageIndex) => {
    const hist = get().pageHistory[pageIndex];
    return !!hist && hist.index < hist.stack.length - 1;
  },

  markSaved: () => set({ isDirty: false }),

  setSignatures: (signatures) => set({ signatures, signaturesLoaded: true }),
  addSignature: (sig) =>
    set((s) => ({
      signatures: [sig, ...s.signatures.filter((x) => x.signatureId !== sig.signatureId)],
      selectedSignatureId: sig.signatureId,
      signaturesLoaded: true,
    })),
  removeSignature: (signatureId) =>
    set((s) => ({
      signatures: s.signatures.filter((x) => x.signatureId !== signatureId),
      selectedSignatureId:
        s.selectedSignatureId === signatureId ? null : s.selectedSignatureId,
    })),
  setSelectedSignatureId: (selectedSignatureId) => set({ selectedSignatureId }),
  setSignatureModalOpen: (signatureModalOpen) => set({ signatureModalOpen }),
  setSignaturesLoaded: (signaturesLoaded) => set({ signaturesLoaded }),
  getSelectedSignature: () => {
    const { signatures, selectedSignatureId } = get();
    if (!selectedSignatureId) return null;
    return signatures.find((s) => s.signatureId === selectedSignatureId) ?? null;
  },

  setProtection: (protection) => set({ protection }),
  setPasswordProtectModalOpen: (passwordProtectModalOpen) =>
    set({ passwordProtectModalOpen }),
  enableProtection: (algorithm) =>
    set({ protection: { enabled: true, algorithm } }),
  clearProtection: () => {
    clearProtectionPassword();
    set({ protection: null });
  },

  setFlushActiveOverlay: (flushActiveOverlay) => set({ flushActiveOverlay }),
  setClearActiveOverlay: (clearActiveOverlay) => set({ clearActiveOverlay }),
  resumeOverlayCommits: () => set({ suspendOverlayCommits: false }),

  captureAnnotationsForExport: () => {
    if (get().suspendOverlayCommits) {
      return JSON.parse(
        JSON.stringify(mergeAnnotationDrafts(get().pageAnnotations))
      ) as Record<number, FabricJson>;
    }
    const flush = get().flushActiveOverlay;
    try {
      flush?.();
    } catch (err) {
      console.warn('Failed to flush active PDF overlay before export', err);
    }
    // Deep-clone so bake cannot race with later store mutations
    return JSON.parse(
      JSON.stringify(mergeAnnotationDrafts(get().pageAnnotations))
    ) as Record<number, FabricJson>;
  },

  applyFinalizedPdf: ({ sourceBytes, pdfProxy }) => {
    // Block viewport teardown/page-switch from writing live objects back on top
    // of the already-baked PDF (that caused visual duplicates after Finalize).
    set({ suspendOverlayCommits: true });
    try {
      get().clearActiveOverlay?.();
    } catch (err) {
      console.warn('Failed to clear PDF overlay after finalize', err);
    }
    clearAnnotationDrafts();
    const prev = get().pdfProxy;
    if (prev && prev !== pdfProxy) {
      try {
        void (prev as { destroy?: () => void | Promise<void> }).destroy?.();
      } catch {
        /* ignore */
      }
    }
    set({
      sourceBytes,
      pdfProxy,
      pageCount: pdfProxy.numPages,
      pageAnnotations: {},
      pageHistory: {},
      thumbUrls: {},
      isDirty: true,
      overlayEpoch: get().overlayEpoch + 1,
      error: null,
      suspendOverlayCommits: true,
    });
  },
}));
